import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchLinkedInTempOrganizations, 
  connectLinkedInPages,
  type LinkedInTempOrgsResponse,
  type LinkedInOrganization 
} from '../services/linkedinService';

/**
 * Hook to fetch LinkedIn temporary organizations.
 * 
 * Args:
 *   enabled (boolean): Whether to execute the query
 * 
 * Returns:
 *   Query result with organizations data, loading state, and error
 */
export const useLinkedInTempOrganizations = (enabled: boolean = true) => {
  return useQuery<LinkedInTempOrgsResponse, Error>({
    queryKey: ['linkedin-temp-organizations'],
    queryFn: fetchLinkedInTempOrganizations,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection time)
    retry: 2,
    refetchOnWindowFocus: false,
    enabled: enabled, // Only execute the query when enabled
  });
};

/**
 * Hook to connect LinkedIn pages.
 * 
 * Returns:
 *   Mutation with connect function and loading state
 */
export const useConnectLinkedInPages = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ selectedPageIds }: { selectedPageIds: string[] }) => 
      connectLinkedInPages(selectedPageIds),
    onSuccess: () => {
      // Invalidate relevant queries after successful connection
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-temp-organizations'] });
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (error) => {
      console.error('Failed to connect LinkedIn pages:', error);
    },
  });
}; 